import { ref as dbRef, push, set, get, query, orderByChild, limitToLast, runTransaction, serverTimestamp } from 'firebase/database';
import axios from 'axios';
import { db, auth } from '../views/firebase/firebase';
import config from '../utils/EnvironmentSwitcher';

/**
 * Video Storage Service — shared social video feed.
 *
 * Files:    IPFS (own node) via the authenticated MiEMarketApi gateway POST /api/ipfs/upload
 * Metadata: Realtime Database → socialVideos/{videoId}   (readable by every signed-in user, so
 *                                                        an uploaded video shows up for everyone)
 * Likes:    socialVideos/{videoId}/likedBy/{uid} + a transactional `likes` counter
 *
 */

const VIDEOS_PATH = 'socialVideos';

const MIME_BY_EXT = {
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    webm: 'video/webm',
};

const guessMimeType = (file) => {
    if (file.type) return file.type;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    return MIME_BY_EXT[ext] || 'video/mp4';
};

class VideoStorageService {
    constructor() {
        this.maxVideoSize = 100 * 1024 * 1024; // 100MB
    }

    /**
     * Generate a JPEG thumbnail from a video file. Resolves null (never rejects/hangs) if the
     * browser can't decode the file — a missing thumbnail must not block the upload.
     */
    async generateThumbnail(file, timeOffset = 1) {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            const objectUrl = URL.createObjectURL(file);
            let settled = false;
            const finish = (result) => {
                if (settled) return;
                settled = true;
                URL.revokeObjectURL(objectUrl);
                resolve(result);
            };
            const timer = setTimeout(() => finish(null), 8000);

            video.muted = true;
            video.playsInline = true;
            video.preload = 'metadata';

            video.onloadedmetadata = () => {
                video.currentTime = Math.min(timeOffset, (video.duration || 2) / 2);
            };
            video.onseeked = () => {
                try {
                    const canvas = document.createElement('canvas');
                    const scale = Math.min(1, 480 / (video.videoWidth || 480));
                    canvas.width = Math.round((video.videoWidth || 480) * scale);
                    canvas.height = Math.round((video.videoHeight || 270) * scale);
                    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
                    canvas.toBlob((blob) => {
                        clearTimeout(timer);
                        finish(blob ? new File([blob], 'thumb.jpg', { type: 'image/jpeg' }) : null);
                    }, 'image/jpeg', 0.8);
                } catch (e) {
                    clearTimeout(timer);
                    finish(null);
                }
            };
            video.onerror = () => {
                clearTimeout(timer);
                finish(null);
            };
            video.src = objectUrl;
        });
    }

    /** Upload a file to IPFS through the authenticated API gateway, reporting 0–100 progress. */
    async uploadFile(file, onPercent) {
        const idToken = await auth.currentUser?.getIdToken();
        if (!idToken) throw new Error('Please sign in to upload videos');

        const formData = new FormData();
        formData.append('file', file);
        try {
            const { data } = await axios.post(`${config.apiUrl}/api/ipfs/upload`, formData, {
                headers: { Authorization: `Bearer ${idToken}` },
                timeout: 10 * 60 * 1000,
                maxBodyLength: Infinity,
                onUploadProgress: (e) => e.total && onPercent?.((e.loaded / e.total) * 100),
            });
            if (!data?.hash) throw new Error('No CID returned');
            return { hash: data.hash, url: data.url, size: data.size || file.size };
        } catch (error) {
            console.error('IPFS upload failed:', error);
            const reason = error?.response?.data?.error || error?.message || 'unknown error';
            throw new Error(`Failed to upload to IPFS: ${reason}`);
        }
    }

    /** Complete upload: validate → thumbnail → IPFS → shared RTDB record. */
    async uploadVideo(file, metadata, authUser, onProgress) {
        const uid = authUser?.uid || auth.currentUser?.uid;
        if (!uid) throw new Error('Please sign in to upload videos');

        if (file.size > this.maxVideoSize) {
            throw new Error(`Video too large. Max size: ${this.maxVideoSize / 1024 / 1024}MB`);
        }
        onProgress?.({ step: 'validating', progress: 5 });

        onProgress?.({ step: 'thumbnail', progress: 10 });
        const thumbnail = await this.generateThumbnail(file);

        const mimeType = guessMimeType(file);

        const video = await this.uploadFile(file, (pct) =>
            onProgress?.({ step: 'uploading_video', progress: 15 + pct * 0.7 })
        );

        let thumb = null;
        if (thumbnail) {
            onProgress?.({ step: 'uploading_thumbnail', progress: 88 });
            try {
                thumb = await this.uploadFile(thumbnail);
            } catch (e) {
                console.warn('Thumbnail upload failed (continuing without it):', e?.message);
            }
        }

        onProgress?.({ step: 'saving_metadata', progress: 94 });
        const newRef = push(dbRef(db, VIDEOS_PATH));
        const record = {
            url: video.url,
            ipfsHash: video.hash,
            thumbnailUrl: thumb?.url || '',
            caption: metadata?.caption || '',
            tags: metadata?.tags || [],
            mimeType,
            size: video.size,
            likes: 0,
            comments: 0,
            shares: 0,
            timestamp: serverTimestamp(),
            uploadedBy: uid,
            user: {
                id: uid,
                username: (authUser?.displayName || 'user').toLowerCase().replace(/\s+/g, ''),
                displayName: authUser?.displayName || 'Anonymous User',
                avatar: authUser?.photoURL || '',
            },
        };
        try {
            await set(newRef, record);
        } catch (error) {
            if (error?.code === 'PERMISSION_DENIED') {
                throw new Error('Saving the video was blocked. Realtime Database rules must allow signed-in users to write socialVideos/.');
            }
            throw error;
        }

        onProgress?.({ step: 'complete', progress: 100 });
        return {
            id: newRef.key,
            fhirId: newRef.key, // kept for existing callers
            url: video.url,
            thumbnailUrl: thumb?.url || '',
            size: video.size,
            ipfsHash: video.hash,
        };
    }

    /** Fetch the newest videos (shared by all users), newest first. */
    async fetchVideos(limit = 20) {
        try {
            const snap = await get(query(dbRef(db, VIDEOS_PATH), orderByChild('timestamp'), limitToLast(limit)));
            const uid = auth.currentUser?.uid;
            const videos = [];
            snap.forEach((child) => {
                videos.push(this.transformRecord(child.key, child.val(), uid));
            });
            return videos.reverse();
        } catch (error) {
            console.error('Failed to fetch videos:', error);
            return [];
        }
    }

    transformRecord(id, v, uid) {
        return {
            id,
            url: v.url,
            thumbnailUrl: v.thumbnailUrl || '',
            caption: v.caption || '',
            tags: Array.isArray(v.tags) ? v.tags : [],
            likes: v.likes || 0,
            comments: v.comments || 0,
            shares: v.shares || 0,
            timestamp: new Date(typeof v.timestamp === 'number' ? v.timestamp : Date.now()),
            size: v.size,
            mimeType: v.mimeType,
            isLiked: !!(uid && v.likedBy && v.likedBy[uid]),
            user: v.user || { id: v.uploadedBy || 'unknown', username: 'user', displayName: 'Anonymous User', avatar: '' },
        };
    }

    /** Like/unlike a video. One like per user; the counter is updated transactionally. */
    async updateVideoEngagement(videoId, engagementType, increment = true) {
        try {
            const uid = auth.currentUser?.uid;
            if (!uid) return false;

            if (engagementType === 'likes') {
                await set(dbRef(db, `${VIDEOS_PATH}/${videoId}/likedBy/${uid}`), increment ? true : null);
            }
            await runTransaction(dbRef(db, `${VIDEOS_PATH}/${videoId}/${engagementType}`), (current) =>
                Math.max(0, (current || 0) + (increment ? 1 : -1))
            );
            return true;
        } catch (error) {
            console.error('Failed to update engagement:', error);
            return false;
        }
    }
}

export default new VideoStorageService();
